import classNames from 'classnames';
import debounce from 'lodash/debounce';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AnchorLink from '@jpp/atoms/AnchorLink/AnchorLink';
import Heading from '@jpp/atoms/Heading';
import { EIconType } from '@jpp/atoms/Icon/utils/types';
import RefImage from '@jpp/atoms/Image';
import { EProductCtaText } from '@jpp/atoms/ProductCta/constants';
import ProductPrice from '@jpp/atoms/ProductPrice';
import { QuantitySelector } from '@jpp/atoms/QuantitySelector/QuantitySelector';
import useIsMounted from '@jpp/hooks/useIsMounted';
import IconAction from '@jpp/molecules/IconAction';
import ContainerLoader from '@jpp/organisms/Loader/ContainerLoader';
import { IReduxState } from 'common/redux/createStore';
import { updateItemInCart } from 'common/redux/wooCommerce/cart/actions';
import { ICartLineItem } from 'common/redux/wooCommerce/cart/reducer';
import { IProduct } from 'common/redux/wooCommerce/products/reducer';
import { getAllStoreProducts } from 'common/redux/wooCommerce/products/selectors';
import {
  trackGoogleRemoveFromCart,
  trackGoogleUpdateCart,
} from 'common/tracking/cart';
import {
  trackRemoveProductInCart,
  trackUpdateProductInCart,
} from 'common/tracking/product';
import { EBillingPeriod, EPageType, ESize } from 'common/typings/enums';
import { EProductStock } from 'common/typings/enums/product';
import { GIFT_CARD_PREFIX } from 'common/utils/constants';
import { capitalizeFirstCharacter } from 'common/utils/shared';
import { isSubscriptionProduct } from 'common/utils/shared/cart';
import {
  getPriceWithCurrency,
  getPriceWithoutCurrency,
} from 'common/utils/shared/product';

import './CartProductCard.scss';

interface ICartProductCardProps {
  className?: string;
  product: ICartLineItem;
}

const ROOT_CLASSNAME = 'CartProductCard';

const getProductSlug = (product: ICartLineItem) => {
  if (product.sku.includes(GIFT_CARD_PREFIX)) {
    return 'e-gift-cards';
  }
  return product.slug;
};

const handleUpdateTracking = (
  title: string,
  id: number,
  sku: string,
  updatePrice: string,
  qty: number,
  slug: string,
  image: string
) => {
  trackUpdateProductInCart(title, id, sku, updatePrice, qty, slug, image);
  trackGoogleUpdateCart(title, id, sku, updatePrice, qty);
};

const handleRemoveTracking = (
  title: string,
  id: number,
  sku: string,
  removePrice: string,
  slug: string,
  image: string
) => {
  trackRemoveProductInCart(title, id, sku, removePrice, slug, image);
  trackGoogleRemoveFromCart(title, id, sku, removePrice);
};

const CartProductCard: React.FunctionComponent<ICartProductCardProps> = ({
  className,
  product: {
    key,
    product_image,
    quantity,
    product_name,
    product_title,
    product_price,
    line_total,
    line_tax,
    product_link,
    subscription_data,
    billing_interval,
    billing_period,
    isBundleItem,
    isPrimaryBundleProduct,
  },
  product,
}) => {
  const slug = getProductSlug(product);
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const allProducts = useSelector<IReduxState, IProduct[]>((state) =>
    getAllStoreProducts(state)
  );
  const findProduct = allProducts?.find(
    (p) => String(p.id) === String(product.product_id)
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const price = line_total + line_tax;
  const priceWithCurrency = getPriceWithCurrency(price);
  const productPrice = product_price;
  const hasProductPrice = parseFloat(getPriceWithoutCurrency(productPrice)) > 0;
  const isSubscribeAndSave = isSubscriptionProduct(product);
  const stockStatus = product?.stock_status.status;
  const isOnBackorder =
    stockStatus?.includes('backorder') ||
    findProduct?.stock_status === EProductStock.OnBackorder;
  const stockQty = product.stock_status.stock_quantity;

  const isOutOfStock =
    stockStatus?.toLowerCase() !== 'in stock' && stockQty <= 0;
  const productTitle = `${product_title} ${
    isOutOfStock && !isOnBackorder
      ? `<span class='${ROOT_CLASSNAME}__heading-oos'>
      (${EProductCtaText.SoldOut})
    </span>`
      : ''
  }`;

  const handleQuantityOnChange = async (qty: number) => {
    if (quantity === qty) {
      return;
    }
    setIsLoading(true);
    await dispatch(updateItemInCart({ cartItemKey: key, quantity: qty }));
    handleUpdateTracking(
      product_title,
      product.product_id,
      product.sku,
      product.product_price,
      qty,
      product.slug,
      product.product_image
    );
    setIsLoading(false);
  };

  const handleDeleteItem = async () => {
    await setIsLoading(true);
    const confirmation = confirm(
      'Are you sure you want to delete all quantities of this item?'
    );
    if (!confirmation) {
      setIsLoading(false);
      return;
    }
    await dispatch(updateItemInCart({ cartItemKey: key, quantity: 0 }));
    handleRemoveTracking(
      product_title,
      product.product_id,
      product.sku,
      product.product_price,
      product.slug,
      product.product_image
    );
    if (isMounted.current) {
      setIsLoading(false);
    }
  };

  const href =
    slug || product_link?.split('/').pop() || product_name?.toLocaleLowerCase();

  useEffect(() => {
    return () => {
      if (isMounted.current) {
        setIsLoading(false);
      }
    };
  }, []);

  if (!product) {
    return null;
  }

  const billingPeriod =
    billing_period || subscription_data?.billing_period.period;
  const billingInterval =
    billing_interval || subscription_data?.billing_period.interval;
  const billingText =
    billingPeriod === EBillingPeriod.Week
      ? `/ Every ${
          String(billingInterval) === '1'
            ? billingPeriod
            : `${billingInterval} ${billingPeriod}s`
        }`
      : `/ ${capitalizeFirstCharacter(billingPeriod)}`;

  return (
    <section
      className={classNames(ROOT_CLASSNAME, className, {
        [`${ROOT_CLASSNAME}--is-out-of-stock`]: isOutOfStock && !isOnBackorder,
        [`${ROOT_CLASSNAME}--is-bundle-item`]: isBundleItem,
        [`${ROOT_CLASSNAME}--is-primary-bundle-item`]: isPrimaryBundleProduct,
        [`${ROOT_CLASSNAME}--is-standard-item`]:
          !isBundleItem && !isPrimaryBundleProduct,
      })}
    >
      <ContainerLoader isVisible={isLoading} />
      {!isBundleItem && (
        <IconAction
          icon={EIconType.Close}
          onClick={handleDeleteItem}
          className={`${ROOT_CLASSNAME}__close`}
        />
      )}
      <div className={`${ROOT_CLASSNAME}__image-wrapper`}>
        {product_image && (
          <RefImage
            className={`${ROOT_CLASSNAME}__image`}
            image={
              {
                url: product_image,
                alt: `${productTitle} image`,
              } as Core.IMedia
            }
          />
        )}
      </div>

      <div className={`${ROOT_CLASSNAME}__content-wrapper`}>
        <div className={`${ROOT_CLASSNAME}__content`}>
          {isSubscribeAndSave ? (
            <Heading
              className={`${ROOT_CLASSNAME}__heading`}
              priority={3}
              children={productTitle}
            />
          ) : (
            <AnchorLink
              className={`${ROOT_CLASSNAME}__link`}
              href={`/${EPageType.Product}/${href}`}
            >
              <Heading
                className={`${ROOT_CLASSNAME}__heading`}
                priority={3}
                children={productTitle}
              />
            </AnchorLink>
          )}

          <p className={`${ROOT_CLASSNAME}__quantity`}>
            Qty: {quantity}{' '}
            {isBundleItem && (
              <>
                <small>(Bundle Item)</small>
              </>
            )}
          </p>

          {!isBundleItem && (
            <p className={`${ROOT_CLASSNAME}__price`}>
              Price:{' '}
              {isSubscribeAndSave
                ? `${priceWithCurrency} ${billingText}`
                : priceWithCurrency}
              {hasProductPrice && !isSubscribeAndSave && (
                <span className={`${ROOT_CLASSNAME}__price-per-qty`}>
                  {findProduct ? (
                    <>
                      <ProductPrice
                        regularPrice={findProduct.regular_price}
                        price={findProduct.price}
                        quantity={1}
                      />
                      &nbsp;each
                    </>
                  ) : (
                    `(${productPrice} each)`
                  )}
                </span>
              )}
            </p>
          )}
          {!isSubscribeAndSave && !isBundleItem && (
            <QuantitySelector
              className={`${ROOT_CLASSNAME}__quantity-selector`}
              currentAmount={quantity}
              onChange={debounce(handleQuantityOnChange, 350)}
              disabled={isLoading}
              size={ESize.Sm}
              maxAmount={stockQty}
              isOnBackorder={isOnBackorder}
              renderedBy={EPageType.Cart}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default CartProductCard;
